<script setup>

import PickStock from "@/components/warehouse/PickStock"
import SlideInDrawer from "@/components/common/navigation/SlideInDrawer";
import Table from "@/components/common/other/Table";
import DocumentUpload from "@/components/common/other/DocumentUpload";
import Button from "@/components/common/input/Button";

import {computed, inject, onMounted, ref} from 'vue';
import DropDownTextArea from "@/components/common/input/DropDownTextArea.vue";
import GridCellDetail from "@/components/common/other/GridCellDetail.vue";
import API from "@/classes/global/API";
import PickStockSave from "@/classes/stock/PickStockSave";
import PickStockViewModel from "@/classes/viewmodels/PickStock";
import {useRoute, useRouter} from "vue-router";
import Modal from "@/components/common/other/Modal.vue";

const pickStock = inject("pickStock");
const updateGridRender = ref();

let editPickStock = ref(new PickStockViewModel());

const route = useRoute();

const pickStockModel = computed({

  get() {

    if (route.params.id === 'new') {
      return pickStock.value
    }
    return editPickStock.value
  },

  set(value) {

    // New freight booking sent from multibooking update:modelValue
    pickStockModel.value = value;
  }

})

async function handleUnpick(rowIndex, row, cancelUnPick = null) {

  if (pickStockModel.value.warehouseJobNumber) {

    if (pickStockModel.value.pickedItems.length === 1) {

      pickStockModel.value.openNoPicksModel = true;
      pickStockModel.value.openPicksDrawer = false;

      if (cancelUnPick) {

        // Keep last stock line, cancel unpick
        pickStockModel.value.openNoPicksModel = false;
        return;
      }

      if (cancelUnPick === false) {

        // Remove last line item, Whjob remains with no stock lines
        unpickStockFromDb(pickStockModel.value.pickedItems[0]);

        pickStockModel.value.openNoPicksModel = false;
        addStockBackToSearchGrid(pickStockModel.value.pickedItems[0]);

        removeStockFromGrid(rowIndex);

        return;
      }

    } else {

      // Remove stock line as more than one remains
      unpickStockFromDb(row);

      removeStockFromGrid(rowIndex);

      addStockBackToSearchGrid(row);

    }

  } else {

    removeStockFromGrid(rowIndex);

    addStockBackToSearchGrid(row);
  }

}

async function unpickStockFromDb(row) {

  await API.unPickStock(row);
}

function removeStockFromGrid(rowIndex) {

  pickStockModel.value.pickedItems.splice(rowIndex, 1);
  updateGridRender.value = pickStockModel.value.pickedItems.length;


}

function addStockBackToSearchGrid(row) {

  let existingPickedItem = pickStockModel.value.searchData.find((item) => {
    return row.skuComposite === item.skuComposite && row.warehouse === item.warehouse;
  })

  if (existingPickedItem) {

    existingPickedItem.available = existingPickedItem.available + row.allocated;
    pickStockModel.value.updateSearchGrid = Math.random().toString(36).substring(2, 15);

    existingPickedItem.zero = '#fff';
  }
}

async function handlePickSave() {

  let saveDetail = new PickStockSave(pickStockModel.value);

  saveDetail.handlePickSave();

  // handlePickStockSearch();

  updateGridRender.value = Math.random().toString(36).substring(2, 15);

}

async function handlePickStockSearch() {

  const response = await API.searchStock(pickStockModel.value.searchCriteria, 'pickstock');

  pickStockModel.value.searchData = response;

}

</script>

<template>
  <SlideInDrawer
      :height="395"
      title="Picked Stock"
      v-model:open-drawer="pickStockModel.openPicksDrawer">
    <div>Warehouse Job Number:
      <span style=" font-weight: bold; color: var(--aqua-blue-definition)"> {{ pickStockModel.warehouseJobNumber }}
    </span>
    </div>
    <div class="view-pick-stock" style="margin-top: 10px;">
      <Table
          :key="updateGridRender"
          :allowGridMenu="false"
          height="230"
          :page-size="180"
          :selectable="false"
          :row-selection="false"
          :dataSource="pickStockModel.pickedItems"
          :columns="pickStockModel.pickedItemGridColumns">
        <template v-slot:unPick="row">

          <div style="margin-left: 20px; font-size: 18px; cursor: pointer"
               @click="handleUnpick(row.rowIndex, row.grid)">&#128259
          </div>
          <!--          <i class="fa-solid fa-rotate-reverse view-pick-stock_table-unpick-icon" ></i>-->
        </template>
        <template v-slot:reOrderLevel="row">
          <div v-if="row.rowData.reOrderLevel.length > 0">
            {{ row.rowData.reOrderLevel[0].reOrderLevel }}
          </div>
        </template>
        <template v-slot:stockSaved="row">
          <div style="margin-left:20px; font-size: 14px;">
            <GridCellDetail v-if="row.rowData.stockSaved">
              <template #details>
                {{ row.rowData.message }}
              </template>
              <template #cell-data>
                <div style="color:#0b8a37; font-size: 21px">&#128504</div>
              </template>
            </GridCellDetail>
            <GridCellDetail v-else>
              <template #details>
                {{ row.rowData.message }}
              </template>
              <template #cell-data>
                <div style="color: #dc620e; font-size: 18px">&#128712</div>
              </template>
            </GridCellDetail>
          </div>
        </template>
        <template v-slot:comments="row">
          <DropDownTextArea
              v-model="row.rowData.comment"
              :width="240">
          </DropDownTextArea>
        </template>
      </Table>
    </div>
    <template #footer>
      <Button
          @click="handlePickSave()"
          style="margin-right: 20px;"
          :disabled="!pickStockModel.consignee.name"
          text="Save & Submit">
      </Button>
    </template>
  </SlideInDrawer>
  <SlideInDrawer
      :height="320"
      title="Upload Documents"
      v-model:open-drawer="pickStockModel.openDocsDrawer">
    <DocumentUpload v-model="pickStockModel"></DocumentUpload>
  </SlideInDrawer>
  <PickStock
      :key="pickStockModel"
      @update-picked-items="updateGridRender = $event"
      v-model="pickStockModel">
  </PickStock>
  <Modal
      v-model="pickStockModel.openNoPicksModel"
      :showClose="false"
      title="Warning"
      width="480px"
      height="100px">
    <template #body>
      <div class="view-pick-stock_cancel-job">&#128711; You are removing all picked stock from this job!
        <div style="margin-top: 15px; color: #0a0a0a">Do you wish to cancel this job and remove it from your records?
        </div>
        <div style="margin-top: 5px; font-size: 11px; color: #4f4f4f">If you cancel, the last stock will not be
          removed
        </div>
      </div>
    </template>
    <template #footer>
      <Button
          @click="handleUnpick(null,null, true)"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="Cancel"
          type="toolbar">
      </Button>
      <Button
          @click="handleUnpick(null,null, false)"
          icon="<i class='fa-duotone fa-pen-to-square'></i>"
          text="No"
          type="danger">
      </Button>
      <Button
          @click="handleUnpick(null,null, false)"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Yes"
          type="primary">
      </Button>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>

.view-pick-stock {

  &_table-unpick-icon {
    font-size: 18px;
    color: #d56c03;
    margin-left: 20px;
    cursor: pointer;
  }

  &_table-unpick-icon:hover {
    color: #0b8a1b;
  }

  &_cancel-job {
    font-size: 14px;
    color: darkred;
  }

}


</style>
